// src/utils/locationUtils.ts
import { Location } from '../types';

// Convert to 6-character geohash for coarse precision
export const encodeGeohash = (lat: number, lon: number, precision: number = 6): string => {
  const base32 = '0123456789bcdefghjkmnpqrstuvwxyz';
  let latRange = [-90, 90];
  let lonRange = [-180, 180];
  let isLon = true;
  let geohash = '';
  let bits = 0;
  let bit = 0;

  while (geohash.length < precision) {
    if (isLon) {
      const mid = (lonRange[0] + lonRange[1]) / 2;
      if (lon > mid) {
        bits = (bits << 1) + 1;
        lonRange[0] = mid;
      } else {
        bits = bits << 1;
        lonRange[1] = mid;
      }
    } else {
      const mid = (latRange[0] + latRange[1]) / 2;
      if (lat > mid) {
        bits = (bits << 1) + 1;
        latRange[0] = mid;
      } else {
        bits = bits << 1;
        latRange[1] = mid;
      }
    }

    isLon = !isLon;
    bit++;

    if (bit === 5) {
      geohash += base32[bits];
      bits = 0;
      bit = 0;
    }
  }

  return geohash;
};

// Round coordinates to reduce precision (approximately 100m accuracy)
export const roundCoordinates = (lat: number, lon: number): { lat: number; lon: number } => {
  return {
    lat: Math.round(lat * 1000) / 1000, // ~111m precision
    lon: Math.round(lon * 1000) / 1000
  };
};

// Get coarse location by default
export const getCoarseLocation = (location: Location): Location => {
  const rounded = roundCoordinates(location.latitude, location.longitude);
  return {
    ...location,
    latitude: rounded.lat,
    longitude: rounded.lon
  };
};

// Get precise location only when explicitly requested
export const getPreciseLocation = (location: Location): Location => {
  return location;
};

export const formatLocationString = (location: Location, precise: boolean = false): string => {
  const loc = precise ? getPreciseLocation(location) : getCoarseLocation(location);
  return `${loc.latitude.toFixed(precise ? 6 : 3)}, ${loc.longitude.toFixed(precise ? 6 : 3)}`;
};

export const getLocationAccuracyDescription = (accuracy?: number): string => {
  if (!accuracy) return 'Unknown accuracy';
  
  if (accuracy < 10) return 'Very precise (GPS)';
  if (accuracy < 50) return 'Precise';
  if (accuracy < 100) return 'Good';
  if (accuracy < 500) return 'Approximate';
  return 'Rough estimate';
};